import { useEffect, useState } from 'react'

import { useHomePage } from './HomePageProvider'

/**
 * How long we keep showing FirstSteps after the user completes the last
 * card before kicking off the cross-fade. Gives the user a beat to see
 * their last checkmark register before the section starts leaving.
 */
export const FIRST_STEPS_HOLD_MS = 500

export const ALL_SET_HOLD_MS = 2000

/**
 * Which component occupies the slot between the scorecard row and the main
 * content on Home. Strictly one-way once data has arrived:
 *
 *   loading → firstSteps → allSet → insights
 *   loading → insights                          (already completed on first observation)
 */
export type HomeSlotPhase = 'loading' | 'firstSteps' | 'allSet' | 'insights'

/**
 * Drives the FirstSteps → AllSet → Insights handoff on Home.
 *
 * The phase moves forward only — there is no path back to FirstSteps once
 * Insights has shown, even if `firstStepsCompleted` flips false again. This
 * matches the product intent: completing the last task is a one-way
 * commitment to the Insights view for the rest of the session.
 *
 * Visual cross-fades between phases are owned by AnimatePresence at the
 * call site; this hook only sequences the logical transitions.
 */
export function useHomeSlotPhase(
  firstStepsHoldMs: number = FIRST_STEPS_HOLD_MS,
  allSetHoldMs: number = ALL_SET_HOLD_MS,
): HomeSlotPhase {
  const { firstStepsCompleted, firstStepsLoading } = useHomePage()
  const [phase, setPhase] = useState<HomeSlotPhase>('loading')

  useEffect(() => {
    if (firstStepsLoading) return
    if (phase === 'loading') {
      setPhase(firstStepsCompleted ? 'insights' : 'firstSteps')
    }
  }, [firstStepsLoading, firstStepsCompleted, phase])

  useEffect(() => {
    if (phase !== 'firstSteps' || !firstStepsCompleted) return
    const timer = setTimeout((): void => setPhase('allSet'), firstStepsHoldMs)
    return (): void => clearTimeout(timer)
  }, [phase, firstStepsCompleted, firstStepsHoldMs])

  useEffect(() => {
    if (phase !== 'allSet') return
    const timer = setTimeout((): void => setPhase('insights'), allSetHoldMs)
    return (): void => clearTimeout(timer)
  }, [phase, allSetHoldMs])

  return phase
}
